import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useUnreadMessages } from "../hooks/useUnreadMessages";
import { SocialMediaBar } from "./SocialMediaBar";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { 
  LayoutDashboard, 
  Users, 
  CreditCard, 
  FileText, 
  MessageSquare, 
  BarChart3, 
  AlertTriangle,
  LogOut,
  Menu,
  X,
  User,
  BookOpen,
  Calendar,
  Calculator,
  MessageCircle,
  Wrench,
  Zap,
  Package,
  Briefcase,
  GraduationCap,
  UserCog,
  Shield,
  ClipboardCheck,
  StickyNote,
  Library,
  Star,
  Lightbulb,
  ShoppingBag,
  Bot
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  LayoutDashboard,
  Users,
  CreditCard,
  FileText,
  MessageSquare,
  BarChart3,
  AlertTriangle,
  User,
  BookOpen,
  Calendar,
  Calculator,
  MessageCircle,
  Wrench,
  Zap,
  Package,
  Briefcase,
  GraduationCap,
  UserCog,
  Shield,
  ClipboardCheck,
  StickyNote,
  Library,
  Star,
  Lightbulb,
  ShoppingBag,
  Bot
};

const getIcon = (iconName) => {
  return iconMap[iconName] || FileText;
};

// Map area IDs to routes for permission-based filtering
const AREA_ROUTE_MAP = {
  dashboard: "/admin",
  participants: "/admin/participants",
  users: "/admin/users",
  courses: "/admin/courses",
  kvp: "/admin/kvp",
  statistics: "/admin/statistics",
  messages: "/admin/messages",
  warnings: "/admin/warnings",
  settings: "/admin/settings",
  content: "/admin/content",
  qm: "/admin/qm",
};

export const AdminLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pages, setPages] = useState([]);
  const [allowedAreas, setAllowedAreas] = useState(null);
  const [isoTabName, setIsoTabName] = useState("ISO 9001");
  const unreadCount = useUnreadMessages();

  useEffect(() => {
    fetchPages();
    fetchPermissions();
    fetchIsoTabName();
  }, []);

  const fetchPages = async () => {
    try {
      const response = await axios.get(`${API}/pages`);
      setPages(response.data.filter(p => p.is_visible));
    } catch (error) {
      console.error("Failed to fetch pages:", error);
    }
  };

  const fetchPermissions = async () => {
    try {
      const res = await axios.get(`${API}/admin/my-permissions`);
      setAllowedAreas(res.data.allowed_areas || []);
    } catch (error) {
      console.error("Failed to fetch permissions:", error);
    }
  };

  const fetchIsoTabName = async () => {
    try {
      const res = await axios.get(`${API}/settings/iso-tab-name`);
      setIsoTabName(res.data.name || "ISO 9001");
    } catch (error) {
      console.error("Failed to fetch ISO tab name:", error);
    }
  };

  const hasArea = (areaId) => {
    if (!allowedAreas) return true; // loading, show all
    if (user?.role === "super_admin") return true;
    return allowedAreas.includes(areaId);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const allNavItems = [
    { path: "/admin", icon: "LayoutDashboard", label: "Dashboard", area: "dashboard" },
    { path: "/admin/participants", icon: "Users", label: "Alle Teilnehmer", area: "participants" },
    { path: "/admin/users", icon: "UserCog", label: "Benutzerverwaltung", area: "users" },
    { path: "/admin/courses", icon: "GraduationCap", label: "Kursverwaltung", area: "courses" },
    { path: "/admin/kvp", icon: "Lightbulb", label: "KVP-Verwaltung", area: "kvp" },
    { path: "/admin/statistics", icon: "BarChart3", label: "Statistiken", area: "statistics" },
    { path: "/admin/messages", icon: "MessageSquare", label: "Nachrichten", area: "messages" },
    { path: "/admin/warnings", icon: "AlertTriangle", label: "Warnungen", area: "warnings" },
    { path: "/admin/booking-approval", icon: "ClipboardCheck", label: "Buchungsfreigabe", area: "bookings" },
    { path: "/admin/settings", icon: "Wrench", label: "Einstellungen", area: "settings" },
  ];

  // Dozent gets a personal dashboard link at the top
  const isDozent = user?.role === "dozent";
  const staticNavItems = (isDozent
    ? [{ path: "/admin/dozent-dashboard", icon: "LayoutDashboard", label: "Mein Dashboard", area: null }]
    : []
  ).concat(allNavItems.filter((item) => hasArea(item.area)));

  // Group pages by booking type
  const aboPages = pages.filter(p => p.booking_type?.includes("_abo"));
  const intensivPages = pages.filter(p => p.booking_type?.includes("_intensiv"));
  const otherPages = pages.filter(p => 
    p.booking_type && 
    !p.booking_type.includes("_abo") && 
    !p.booking_type.includes("_intensiv")
  );

  const NavItem = ({ path, icon, label }) => {
    const Icon = getIcon(icon);
    const isActive = location.pathname === path;
    
    return (
      <Link
        to={path}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
          isActive 
            ? "bg-primary/10 text-primary border-l-2 border-primary" 
            : "text-muted-foreground hover:text-primary hover:bg-secondary/50"
        }`}
        data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Icon className="h-4 w-4" />
        <span>{label}</span>
      </Link>
    );
  };

  const NavSection = ({ title, children }) => (
    <div className="mb-2">
      <div className="px-4 py-2">
        <span className="text-xs text-muted-foreground uppercase tracking-widest">{title}</span>
      </div>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
          <span className="text-lg font-semibold text-primary">S&T Brain-Trainer</span>
          <div className="w-10" />
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-card border-r border-border transform transition-transform lg:translate-x-0 ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-border">
            <img 
              src="/logo-st.webp" 
              alt="S&T Brain-Trainer" 
              className="h-12 w-auto object-contain mb-2"
              data-testid="admin-sidebar-logo"
            />
            <p className="text-xs text-muted-foreground uppercase tracking-widest">Admin Portal</p>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-2">
            <nav>
              {/* Main Navigation */}
              <NavSection title="Übersicht">
                {staticNavItems.map((item) => (
                  <NavItem
                    key={item.path}
                    {...item}
                    badge={item.path === "/admin/messages" ? unreadCount : undefined}
                  />
                ))}
              </NavSection>
              
              {/* Abonnements */}
              {hasArea("bookings") && aboPages.length > 0 && (
                <NavSection title="Abonnements">
                  {aboPages.map((page) => (
                    <NavItem 
                      key={page.id} 
                      path={`/admin/booking/${page.slug}`} 
                      icon={page.icon} 
                      label={page.name}
                    />
                  ))}
                </NavSection>
              )}
              
              {/* Intensivkurse */}
              {hasArea("bookings") && intensivPages.length > 0 && (
                <NavSection title="Intensivkurse">
                  {intensivPages.map((page) => (
                    <NavItem 
                      key={page.id} 
                      path={`/admin/booking/${page.slug}`} 
                      icon={page.icon} 
                      label={page.name}
                    />
                  ))}
                </NavSection>
              )}

              {/* Weitere */}
              {hasArea("bookings") && otherPages.length > 0 && (
                <NavSection title="Weitere">
                  {otherPages.map((page) => (
                    <NavItem 
                      key={page.id} 
                      path={`/admin/booking/${page.slug}`} 
                      icon={page.icon} 
                      label={page.name}
                    />
                  ))}
                </NavSection>
              )}

              {/* Content Management */}
              {hasArea("content") && (
                <NavSection title="Verwaltung">
                  <NavItem path="/admin/content" icon="FileText" label="Inhalte verwalten" />
                </NavSection>
              )}

              {/* QM Section - based on permissions */}
              {hasArea("qm") && (
                <NavSection title="Qualitätsmanagement">
                  <NavItem path="/admin/qm" icon="Shield" label={`${isoTabName} QM`} />
                </NavSection>
              )}

              {/* Rights Management - only for super_admin */}
              {user?.role === "super_admin" && (
                <NavSection title="Administration">
                  <NavItem path="/admin/rights" icon="Shield" label="Rechteverwaltung" />
                </NavSection>
              )}

              {/* KI-Tools - Meister-Agent für alle Admin/Dozent/Auditor Rollen */}
              <NavSection title="KI-Tools">
                <NavItem path="/admin/meister-agent" icon="GraduationCap" label="Meister-Agent" />
              </NavSection>
            </nav>
          </ScrollArea>

          {/* User Info */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-muted-foreground hover:text-destructive"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Abmelden
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8 space-y-6">
          {/* S&T auf einen Blick - persistent across all admin pages */}
          <SocialMediaBar compact />
          {title && (
            <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight">{title}</h1>
          )}
          {children}
        </div>
      </main>
    </div>
  );
};

export const ParticipantLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accessiblePages, setAccessiblePages] = useState([]);
  const unreadCount = useUnreadMessages();

  useEffect(() => {
    // Determine which pages user has access to based on their bookings
    if (user?.bookings) {
      const bookingTypes = user.bookings
        .filter(b => b.access_allowed)
        .map(b => ({ type: b.booking_type, subType: b.hq_sub_type }));
      
      fetchAccessiblePages(bookingTypes);
    }
  }, [user]);

  const fetchAccessiblePages = async (bookingTypes) => {
    try {
      const response = await axios.get(`${API}/pages`);
      const allPages = response.data.filter(p => p.is_visible);
      
      // Filter pages based on user's bookings
      const accessible = allPages.filter(page => {
        if (!page.booking_type) return false;
        
        return bookingTypes.some(bt => {
          if (bt.type === page.booking_type) {
            // Check HQ sub-type if applicable
            if (page.hq_sub_type) {
              return bt.subType === page.hq_sub_type;
            }
            return true;
          }
          return false;
        });
      });
      
      setAccessiblePages(accessible);
    } catch (error) {
      console.error("Failed to fetch pages:", error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const staticNavItems = [
    { path: "/dashboard", icon: "LayoutDashboard", label: "Dashboard" },
    { path: "/lernportal", icon: "Sparkles", label: "Lernportal" },
    { path: "/meister-agent", icon: "GraduationCap", label: "Meister-Agent" },
    { path: "/lernplattform", icon: "GraduationCap", label: "Lernplattform" },
    { path: "/lernkarten", icon: "BookOpen", label: "Lernkarten" },
    { path: "/medienbibliothek", icon: "Library", label: "Medienbibliothek" },
    { path: "/community", icon: "MessageCircle", label: "Community" },
    { path: "/lernraum", icon: "StickyNote", label: "Mein Lernraum" },
    { path: "/kvp", icon: "Lightbulb", label: "Verbesserungen" },
    { path: "/bewerbung", icon: "Briefcase", label: "Bewerbungs-Tool" },
    { path: "/messages", icon: "MessageSquare", label: "Nachrichten" },
    { path: "/profile", icon: "User", label: "Profil" },
  ];

  const NavItem = ({ path, icon, label, badge }) => {
    const Icon = getIcon(icon);
    const isActive = location.pathname === path;
    
    return (
      <Link
        to={path}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
          isActive 
            ? "bg-primary/10 text-primary border-l-2 border-primary" 
            : "text-muted-foreground hover:text-primary hover:bg-secondary/50"
        }`}
        data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Icon className="h-4 w-4" />
        <span className="flex-1">{label}</span>
        {badge > 0 && (
          <span
            className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold animate-pulse"
            data-testid={`nav-badge-${label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </Link>
    );
  };

  // Get warning status
  const hasWarning = user?.bookings?.some(b => b.status === "expiring_soon");

  return (
    <div className="min-h-screen bg-background">
      {/* Warning Banner */}
      {hasWarning && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-500/15 border-b border-yellow-500/50 px-4 py-2 lg:pl-64">
          <div className="flex items-center justify-center gap-2 text-yellow-500 text-sm">
            <AlertTriangle className="h-4 w-4" />
            <span>Ein Abo läuft bald ab!</span>
          </div>
        </div>
      )}

      {/* Mobile Header */}
      <header className={`lg:hidden fixed ${hasWarning ? 'top-10' : 'top-0'} left-0 right-0 z-40 bg-card border-b border-border px-4 py-3`}>
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
          <span className="text-lg font-semibold text-primary">S&T Brain-Trainer</span>
          <div className="w-10" />
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`fixed ${hasWarning ? 'top-10' : 'top-0'} bottom-0 left-0 z-30 w-64 bg-card border-r border-border transform transition-transform lg:translate-x-0 ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-border">
            <img 
              src="/logo-st.webp" 
              alt="S&T Brain-Trainer" 
              className="h-12 w-auto object-contain mb-2"
              data-testid="participant-sidebar-logo"
            />
            <p className="text-xs text-muted-foreground uppercase tracking-widest">Teilnehmer Portal</p>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-2">
            <nav className="space-y-1">
              {staticNavItems.map((item) => (
                <NavItem
                  key={item.path}
                  {...item}
                  badge={item.path === "/messages" ? unreadCount : undefined}
                />
              ))}
              
              {accessiblePages.length > 0 && (
                <>
                  <div className="px-4 py-3">
                    <span className="text-xs text-muted-foreground uppercase tracking-widest">Meine Inhalte</span>
                  </div>
                  {accessiblePages.map((page) => (
                    <NavItem 
                      key={page.id} 
                      path={`/content/${page.slug}`} 
                      icon={page.icon} 
                      label={page.name}
                    />
                  ))}
                </>
              )}
            </nav>
          </ScrollArea>

          {/* User Info */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-muted-foreground hover:text-destructive"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Abmelden
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 lg:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className={`lg:pl-64 ${hasWarning ? 'pt-26 lg:pt-10' : 'pt-16 lg:pt-0'}`}>
        <div className="p-6 lg:p-8">
          {title && (
            <h1 className="text-2xl lg:text-3xl font-semibold mb-6 tracking-tight">{title}</h1>
          )}
          {children}
        </div>
      </main>
    </div>
  );
};

